import { Component, OnDestroy, OnInit, ViewEncapsulation, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { SidebarConstants } from './sidebar.constants';
import { SharedConstants } from '../shared.constants';
import { AnalyticsService } from '../../core/analytics';
import { NavigationService } from '../../navigation.service';
import { AppConstants } from '../../app.constants';
import { LocationSwitcherModule } from '../location-switcher/location-switcher.module';
import { UserLocation } from '../../models/user/location.model';
import { SidebarNavLinkComponent } from './sidebar-nav-link/sidebar-nav-link.component';
import { readSidebarCollapsed, writeSidebarCollapsed } from './sidebar-collapsed-storage';
import * as fromRoot from '../../reducers';
import { rewriteLocationSegment, stripLocationPrefix } from '../navbar/location-utils';

@Component({
  selector: 'sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, RouterLink, RouterLinkActive, MatIconModule, MatTooltipModule, LocationSwitcherModule, SidebarNavLinkComponent]
})
export class SidebarComponent implements OnInit, OnDestroy {
    @Output() public sidebarToggled = new EventEmitter<boolean>();

    public navLinks: any[];
    public isCollapsed: boolean = readSidebarCollapsed();
    public isMobile$: Observable<boolean>;
    public isMobileMenuOpen: boolean = false;
    public isMobileAccountOpen: boolean = false;
    public showMobileLocationSwitcher: boolean = true;
    public unreadNotificationCount$: Observable<number>;
    public selectedLocation$: Observable<UserLocation>;
    public selectedLocationNumber: string;
    public readonly feedbackUrl = AppConstants.feedbackUrl;
    public meetAnalyticsHighlighted = false;
    private isNextGen: boolean = false; // SL-1866: gates the Meet/Analytics highlight fix on the legacy drilldowns
    private wasAutoExpanded: boolean = false; // Track if sidebar was auto-expanded from collapsed state
    private lastSelectedNavIndex: number = -1; // Track the last selected parent nav index

    private disableDiscoverTab$: Observable<boolean>;
    private disableMeetTab$: Observable<boolean>;
    private disableRecoverTab$: Observable<boolean>;
    private disableEngageTab$: Observable<boolean>;
    private disableCalendarTab$: Observable<boolean>;
    public templateManagement$: Observable<boolean>;
    public isAdminUser$: Observable<boolean>
    private enableDashboard$: Observable<boolean>;
    private flagSub: Subscription = null;
    private routerSub: Subscription = null;
    private selectedLocationSub: Subscription = null;

    private readonly meetDrilldownDetailIds = new Set([
      'customerTransactions',
      'cateringDetails'
    ]);

    constructor(
        private analytics: AnalyticsService,
        private router: Router,
        private store: Store<fromRoot.State>,
        private navService: NavigationService
    ) {}

    public ngOnInit() {
      this.isMobile$ = this.store.select(fromRoot.getIsMobile);
      this.unreadNotificationCount$ = this.store.select(fromRoot.getNotificationUnreadCount);
      this.selectedLocation$ = this.store.select(fromRoot.getSelectedLocation);
      this.selectedLocationSub = this.selectedLocation$.subscribe((location) => {
        this.selectedLocationNumber = location?.locationNumber;
      });
      this.disableDiscoverTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableDiscoverTabFlag));
      this.disableMeetTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableMeetTabFlag));
      this.disableRecoverTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableRecoverTabFlag));
      this.disableEngageTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableEngageTabFlag));
      this.disableCalendarTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableCalendarTabFlag));
      this.templateManagement$ = this.store.select(fromRoot.getFeatureEnabled('template-management-tab'))
      this.isAdminUser$ = this.store.select(fromRoot.getAdminFull)
      this.enableDashboard$ = this.store.select(fromRoot.getFeatureEnabled('next-gen-dashboard'))
      const isNextGen$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.displaySidenav));

      this.flagSub = combineLatest([
        this.disableDiscoverTab$,
        this.disableMeetTab$,
        this.disableRecoverTab$,
        this.disableEngageTab$,
        this.disableCalendarTab$,
        this.templateManagement$,
        this.isAdminUser$,
        this.enableDashboard$,
        isNextGen$
      ]).pipe(take(1)).subscribe(([
        disableDiscoverTab,
        disableMeetTab,
        disableRecoverTab,
        disableEngageTab,
        disableCalendarTab,
        templateManagement,
        isAdminUser,
        enableDashboard,
        isNextGen
      ]) => {
        this.isNextGen = isNextGen;
        this.navLinks = SidebarConstants.navbarTabs(
          disableDiscoverTab,
          disableMeetTab,
          disableRecoverTab,
          disableEngageTab,
          disableCalendarTab,
          (templateManagement && isAdminUser),
          enableDashboard
        );
      });

      this.refreshMeetAnalyticsHighlight();
      this.routerSub = this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd)
      ).subscribe(() => this.refreshMeetAnalyticsHighlight());
    }

    public isMeetAnalyticsSubmenuActive(subItem: { url: string }, routerLinkActive: boolean): boolean {
      if (subItem.url !== '/meet/analytics') {
        return routerLinkActive;
      }
      // Gated behind next-gen (SL-1866): flag off keeps today's behavior (highlight drops on the legacy drilldowns).
      return routerLinkActive || (this.isNextGen && this.meetAnalyticsHighlighted);
    }

    private refreshMeetAnalyticsHighlight(): void {
      const url = stripLocationPrefix(this.router.url);
      this.showMobileLocationSwitcher = !/^\/engage\/(?:campaign-details|engage-details)(?:[/?#;]|$)/.test(url);

      if (url.startsWith('/meet/analytics')) {
        this.meetAnalyticsHighlighted = true;
        return;
      }

      if (!url.startsWith('/understand/detail')) {
        this.meetAnalyticsHighlighted = false;
        return;
      }

      const idMatch = /[?&]id=([^&]+)/.exec(url);
      const detailId = idMatch ? decodeURIComponent(idMatch[1]) : '';
      this.meetAnalyticsHighlighted = this.meetDrilldownDetailIds.has(detailId);
    }

    public toggleSidebar(): void {
      this.isCollapsed = !this.isCollapsed;
      this.sidebarToggled.emit(this.isCollapsed);
      // Only manual toggles are persisted; auto-expanding to open a submenu is transient
      writeSidebarCollapsed(this.isCollapsed);

      // Reset auto-expanded flag when manually toggling
      this.wasAutoExpanded = false;

      if (this.isCollapsed && this.navLinks) {
        // Collapse all submenus when sidebar is collapsed
        this.navLinks.forEach(link => {
          if (link.hasSubmenu) {
            link.expanded = false;
          }
        });
      } else if (!this.isCollapsed && this.lastSelectedNavIndex >= 0 && this.navLinks) {
        // When expanding, restore the last selected nav item's expanded state
        this.navLinks.forEach((link, i) => {
          if (link.hasSubmenu) {
            link.expanded = i === this.lastSelectedNavIndex;
          }
        });
      }
    }

    public onNavItemClick(link: any, index: number): void {
      if (link.hasSubmenu) {
        // Track the selected nav item
        this.lastSelectedNavIndex = index;

        // If sidebar is collapsed, expand it and open the submenu
        if (this.isCollapsed) {
          this.isCollapsed = false;
          this.wasAutoExpanded = true; // Mark as auto-expanded
          this.sidebarToggled.emit(this.isCollapsed);
          // Collapse all other submenus and expand the clicked one
          this.navLinks.forEach((navLink, i) => {
            if (navLink.hasSubmenu) {
              navLink.expanded = i === index;
            }
          });
        } else {
          link.expanded = !link.expanded;
        }
      } else if (link.external) {
        // Use full page navigation for external links (e.g., Vercel reroutes)
        globalThis.location.href = this.locationScopedUrl(link.url);
      } else {
        this.setActiveNavItem(index);
        this.router.navigate([link.url]);
      }

      this.analytics.sendEvent('Sidebar Navigation Click', {
        'Nav Item': link.label
      });
    }

    private setActiveNavItem(index: number): void {
      if (this.navLinks) {
        this.navLinks.forEach((link, i) => {
          link.active = i === index;
        });
      }
    }

    public onSubmenuItemClick(subItem: any, parentIndex: number): void {
      // Track the parent nav item
      this.lastSelectedNavIndex = parentIndex;

      // Navigate to the subitem route
      this.router.navigate([subItem.url], { queryParams: subItem.queryParams });

      // Only collapse the sidebar if it was auto-expanded from collapsed state
      if (this.wasAutoExpanded) {
        this.isCollapsed = true;
        this.sidebarToggled.emit(this.isCollapsed);
        this.wasAutoExpanded = false;
        if (this.navLinks) {
          this.navLinks.forEach(link => {
            if (link.hasSubmenu) {
              link.expanded = false;
            }
          });
        }
      }

      this.analytics.sendEvent('Sidebar Submenu Navigation Click', {
        'Submenu Item': subItem.label
      });
    }

    public toggleMobileMenu(): void {
      if (!this.isMobileMenuOpen) {
        this.closeMobileAccount();
      }
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    }

    public closeMobileMenu(): void {
      this.isMobileMenuOpen = false;
    }

    public toggleMobileAccount(): void {
      if (!this.isMobileAccountOpen) {
        this.closeMobileMenu();
        this.analytics.sendEvent('Mobile Account Menu Open', {});
      }
      this.isMobileAccountOpen = !this.isMobileAccountOpen;
    }

    public closeMobileAccount(): void {
      this.isMobileAccountOpen = false;
    }

    public closeMobileOverlays(): void {
      this.closeMobileMenu();
      this.closeMobileAccount();
    }

    public onMobileLogoClick(): void {
      this.closeMobileOverlays();
      this.analytics.sendEvent('Mobile Logo Click', {});
    }

    public openSettings(): void {
      this.analytics.sendEvent('Settings Click', {});
      this.router.navigate(['/settings']);
      this.closeMobileAccount();
    }

    public openNotifications(): void {
      this.analytics.sendEvent('Notifications Click', {});
      this.router.navigate(['/notifications']);
      this.closeMobileAccount();
    }

    public openGiveFeedback(): void {
      this.analytics.sendEvent('Give Feedback Click', {});
      window.open(this.feedbackUrl, '_blank');
      this.closeMobileAccount();
    }

    public onMobileNavItemClick(link: any, index: number): void {
      if (link.hasSubmenu) {
        link.expanded = !link.expanded;
      } else if (link.external) {
        // Use full page navigation for external links (e.g., Vercel reroutes)
        globalThis.location.href = this.locationScopedUrl(link.url);
      } else {
        this.setActiveNavItem(index);
        this.router.navigate([link.url]);
        this.closeMobileMenu();
      }

      this.analytics.sendEvent('Mobile Sidebar Navigation Click', {
        'Nav Item': link.label
      });
    }

    public logoutUser(): void {
      this.analytics.sendEvent('Logout Click', {});
      this.navService.signOut();
      this.closeMobileOverlays();
    }

    // routerLinkActive compares the raw routerLink target against the router's current
    // (always location-prefixed) URL via exact UrlTree equality - it never runs the
    // redirect guard that would otherwise add the prefix, so nav items must carry it
    // themselves for "active" highlighting to work at all.
    public locationScopedUrl(url: string): string {
      return this.selectedLocationNumber ? rewriteLocationSegment(url, this.selectedLocationNumber) : url;
    }

    public ngOnDestroy() {
      if (this.flagSub) {
        this.flagSub.unsubscribe();
      }
      if (this.routerSub) {
        this.routerSub.unsubscribe();
      }
      if (this.selectedLocationSub) {
        this.selectedLocationSub.unsubscribe();
      }
    }
}
